import React, { useState, useEffect } from 'react';
import { Typography, Button, Box, TextField, Slider } from '@mui/material';
import ModalWrapper from '../components/ModalWrapper';

interface PartialPickModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (picked: number) => void;
  groceryName: string;
  quantity: number;
  currentPicked: number;
}

const PartialPickModal: React.FC<PartialPickModalProps> = ({
  open,
  onClose,
  onConfirm,
  groceryName,
  quantity,
  currentPicked,
}) => {
  const [picked, setPicked] = useState(currentPicked);

  useEffect(() => {
    if (open) {
      // If currently unpicked, default to full quantity; otherwise use current value
      setPicked(currentPicked === 0 ? quantity : currentPicked);
    }
  }, [open, currentPicked, quantity]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    setPicked(value as number);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= quantity) {
      setPicked(value);
    }
  };

  const handleConfirm = () => {
    onConfirm(picked);
    onClose();
  };

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>How many did you pick?</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {groceryName} — {quantity} needed
      </Typography>

      <Box sx={{ mt: 3, px: 2 }}>
        <Slider
          value={picked}
          onChange={handleSliderChange}
          min={0}
          max={quantity}
          step={1}
          marks
          valueLabelDisplay="on"
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button variant="outlined" size="small" onClick={() => setPicked(0)}>None</Button>
        <TextField
          type="number"
          value={picked}
          onChange={handleInputChange}
          inputProps={{ min: 0, max: quantity, step: 1 }}
          sx={{ width: 80 }}
          size="small"
        />
        <Button variant="outlined" size="small" onClick={() => setPicked(quantity)}>All</Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleConfirm}>
          Confirm
        </Button>
      </Box>
    </ModalWrapper>
  );
};

export default PartialPickModal;